// Karta widgetu: hlavička, vykreslení, zdroj a akce. Používá ji chat i stránka pohledu.
import { useMemo, useState, type ReactNode } from 'react';
import { BookmarkSimpleIcon, CheckIcon, WarningIcon } from '@phosphor-icons/react';
import { Button, Skeleton, useToast } from '@smworks-cz/ui-kit';
import { computeWidget, parseWidget, sourceLabel, type DatasetSnapshot, type ViewDTO, type WidgetSpec } from '@sam/shared';
import { WidgetView } from './WidgetView';
import { api } from '../../lib/api';
import { keys, queryClient } from '../../lib/queries';
import { fmtTime } from '../../lib/format';
import { navigate } from '../../lib/router';

export function WidgetSkeleton({ title = 'Připravuji widget…' }: { title?: string }) {
  return (
    <div className="w-card">
      <div className="w-head">
        <h3>{title}</h3>
      </div>
      <Skeleton variant="rect" height={140} />
    </div>
  );
}

function WidgetError({ title, error, raw }: { title?: string; error: string; raw?: string }) {
  return (
    <div className="w-card w-error">
      <div className="w-head">
        <h3>
          <WarningIcon size={14} weight="bold" /> {title ?? 'Widget nejde zobrazit'}
        </h3>
      </div>
      <p className="small muted">{error}</p>
      {raw && (
        <details className="small">
          <summary>Popis widgetu</summary>
          <pre className="mono">{raw}</pre>
        </details>
      )}
    </div>
  );
}

interface CardProps {
  spec: WidgetSpec;
  dataset: DatasetSnapshot;
  actions?: ReactNode;
  compact?: boolean;
  /** Karta na přehledu: tabulka nejvýš tolik řádků. */
  maxRows?: number;
  /** Karta na přehledu (bez rámečku, viz styles.css). */
  tile?: boolean;
  onOpen?: () => void;
  footer?: ReactNode;
}

export function WidgetCard({ spec, dataset, actions, compact, maxRows, tile, onOpen, footer }: CardProps) {
  const r = useMemo(() => computeWidget(spec, dataset.result), [spec, dataset]);
  if (!r.ok) return <WidgetError title={spec.title} error={r.error} />;
  return (
    <div
      className={`w-card${compact ? ' compact' : ''}${tile ? ' tile-card' : ''}${onOpen ? ' clickable' : ''}`}
      onClick={onOpen}
      role={onOpen ? 'link' : undefined}
      aria-label={tile ? spec.title : undefined}
      title={tile ? spec.title : undefined}
    >
      {!tile && (
        <div className="w-head">
          <div>
            <h3>{spec.title}</h3>
            {spec.subtitle && !compact && <p className="small muted">{spec.subtitle}</p>}
          </div>
          {!compact && !tile && (
            <span className="w-src">
              {sourceLabel(dataset.source)} · načteno {fmtTime(dataset.fetchedAt)}
            </span>
          )}
        </div>
      )}
      <WidgetView model={r.model} compact={compact} maxRows={maxRows} tile={tile} />
      {(actions || footer) && (
        <div className="w-foot">
          {footer}
          <span className="grow" />
          {actions}
        </div>
      )}
    </div>
  );
}

/** Widget v odpovědi chatu. */
export function ChatWidget(p: { json: string; closed: boolean; live: boolean; index: number; messageId: string; datasets?: Record<string, DatasetSnapshot> }) {
  const [saved, setSaved] = useState<ViewDTO | null>(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  if (!p.closed || p.live) return <WidgetSkeleton />;
  const parsed = parseWidget(p.json);
  if (!parsed.ok) return <WidgetError error={parsed.error} raw={p.json} />;
  const ds = p.datasets?.[parsed.spec.data];
  if (!ds) return <WidgetError title={parsed.spec.title} error="Data k widgetu nejsou k dispozici (dataset nepatří k této odpovědi nebo vypršel)." />;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api<{ view: ViewDTO }>('POST', '/views', { messageId: p.messageId, index: p.index });
      setSaved(r.view);
      void queryClient.invalidateQueries({ queryKey: keys.views });
      toast({ variant: 'success', title: `Uloženo do Moje pohledy: ${r.view.title}` });
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <WidgetCard
      spec={parsed.spec}
      dataset={ds}
      actions={
        saved ? (
          <Button size="sm" variant="secondary" icon={<CheckIcon size={14} />} onClick={() => navigate(`/v/${saved.id}`)}>
            Otevřít pohled
          </Button>
        ) : (
          <Button size="sm" variant="outline" icon={<BookmarkSimpleIcon size={14} />} loading={saving} onClick={() => void save()}>
            Uložit jako pohled
          </Button>
        )
      }
    />
  );
}
